import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, Button, Table, Spinner, Alert, Form, InputGroup, Row, Col } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../../../contexts/CartContext';
import api from '../../../api/apiClient';

const CartPage = () => {
  const navigate = useNavigate();
  const {
    cartItems,
    refreshCart,
    removeCourseFromCart,
    isLoading,
    voucherCode,
    setVoucherCode
  } = useCart();

  const [removingCourseId, setRemovingCourseId] = useState(null);
  const [voucherInput, setVoucherInput] = useState('');
  const [voucherStatus, setVoucherStatus] = useState(null); // 'success' | 'error' | 'info'
  const [voucherMessage, setVoucherMessage] = useState('');
  const [voucherLoading, setVoucherLoading] = useState(false);
  const [voucherDiscount, setVoucherDiscount] = useState(0);

  useEffect(() => {
    refreshCart();
  }, [refreshCart]);

  useEffect(() => {
    // keep input in sync if voucher was applied elsewhere (e.g. payment page)
    setVoucherInput(voucherCode || '');
  }, [voucherCode]);

  const recalculateVoucher = useCallback(async () => {
    const trimmed = (voucherCode || '').trim();

    if (!trimmed) {
      setVoucherDiscount(0);
      setVoucherStatus(null);
      setVoucherMessage('');
      return;
    }

    if (!cartItems.length) {
      setVoucherDiscount(0);
      setVoucherStatus('info');
      setVoucherMessage('Cart is empty. Voucher will be applied when you add a course.');
      return;
    }

    setVoucherLoading(true);
    try {
      const response = await api.vouchers.list({ code: trimmed });
      const vouchers = response.data?.data || response.data || [];

      if (!Array.isArray(vouchers) || vouchers.length === 0) {
        setVoucherStatus('error');
        setVoucherMessage('Voucher not found.');
        setVoucherDiscount(0);
        return;
      }

      // Prefer exact code match, case-insensitive
      const lowerCode = trimmed.toLowerCase();
      const voucher =
        vouchers.find((v) => v.code?.toLowerCase() === lowerCode) || vouchers[0];

      if (!voucher || voucher.deleted) {
        setVoucherStatus('error');
        setVoucherMessage('Voucher is not available.');
        setVoucherDiscount(0);
        return;
      }

      if (voucher.status !== 'active') {
        setVoucherStatus('error');
        setVoucherMessage('Voucher is inactive.');
        setVoucherDiscount(0);
        return;
      }

      const now = new Date();
      const start = voucher.start_date ? new Date(voucher.start_date) : null;
      const end = voucher.end_date ? new Date(voucher.end_date) : null;

      if ((start && now < start) || (end && now > end)) {
        setVoucherStatus('error');
        setVoucherMessage('Voucher is expired or not yet valid.');
        setVoucherDiscount(0);
        return;
      }

      // Compute applicable total based on scope
      let applicableTotal = 0;

      if (voucher.scope === 'course') {
        const uniqueCourseIds = Array.from(
          new Set(cartItems.map((item) => item.courseId || item.course?.id).filter(Boolean))
        );

        if (
          uniqueCourseIds.length !== 1 ||
          Number(uniqueCourseIds[0]) !== Number(voucher.course_id)
        ) {
          setVoucherStatus('error');
          setVoucherMessage('This voucher can only be used when your cart contains only the matching course.');
          setVoucherDiscount(0);
          return;
        }

        cartItems.forEach((item) => {
          const price = Number(item.course?.price || 0);
          if (item.courseId === voucher.course_id || item.course?.id === voucher.course_id) {
            applicableTotal += price;
          }
        });
      } else {
        cartItems.forEach((item) => {
          const price = Number(item.course?.price || 0);
          applicableTotal += price;
        });
      }

      if (applicableTotal <= 0) {
        setVoucherStatus('error');
        setVoucherMessage('Voucher does not apply to the courses in your cart.');
        setVoucherDiscount(0);
        return;
      }

      let discountAmount = 0;
      if (voucher.discount_type === 'percentage') {
        discountAmount = (applicableTotal * Number(voucher.discount_value || 0)) / 100;
      } else {
        discountAmount = Number(voucher.discount_value || 0);
      }

      if (!Number.isFinite(discountAmount) || discountAmount <= 0) {
        setVoucherStatus('error');
        setVoucherMessage('Voucher does not provide any discount.');
        setVoucherDiscount(0);
        return;
      }

      // Cap discount to applicable total
      discountAmount = Math.min(discountAmount, applicableTotal);

      setVoucherDiscount(discountAmount);
      setVoucherStatus('success');
      setVoucherMessage('Voucher applied successfully. Discount will be applied at checkout.');
    } catch (error) {
      console.error('Error validating voucher on cart page:', error);
      setVoucherStatus('error');
      setVoucherMessage(
        error.response?.data?.message || 'Unable to validate voucher right now.'
      );
      setVoucherDiscount(0);
    } finally {
      setVoucherLoading(false);
    }
  }, [voucherCode, cartItems]);

  useEffect(() => {
    // When cart contents change (e.g. course removed), keep voucher calculation in sync
    if (voucherCode) {
      recalculateVoucher();
    }
  }, [voucherCode, cartItems, recalculateVoucher]);

  const totalAmount = useMemo(() => {
    const total = cartItems.reduce((sum, item) => {
      return sum + Number(item.course?.price || 0);
    }, 0);
    return total.toFixed(2);
  }, [cartItems]);

  const totalAfterDiscount = useMemo(() => {
    const subtotal = Number(totalAmount);
    const discount = Number(voucherDiscount) || 0;
    const finalTotal = Math.max(subtotal - discount, 0);
    return finalTotal.toFixed(2);
  }, [totalAmount, voucherDiscount]);

  const handleRemove = async (courseId) => {
    try {
      setRemovingCourseId(courseId);
      await removeCourseFromCart(courseId);
    } finally {
      setRemovingCourseId(null);
    }
  };

  const handleApplyVoucher = () => {
    const trimmed = (voucherInput || '').trim();
    if (!trimmed) {
      setVoucherStatus('error');
      setVoucherMessage('Please enter a voucher code.');
      return;
    }

    setVoucherCode(trimmed);
    setVoucherStatus(null);
    setVoucherMessage('');
    setVoucherDiscount(0);
    recalculateVoucher();
  };

  const handleRemoveVoucher = () => {
    setVoucherCode('');
    setVoucherInput('');
    setVoucherDiscount(0);
    setVoucherStatus('info');
    setVoucherMessage('Voucher removed.');
  };

  const handlePayNow = () => {
    navigate('/user/payment/select');
  };

  const renderEmptyState = () => (
    <div className="text-center py-12 sm:py-16">
      <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
        <i className="fas fa-shopping-cart text-white text-3xl sm:text-3xl"></i>
      </div>
      <h5 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">Your cart is currently empty</h5>
      <p className="text-gray-600 text-base sm:text-lg mb-6">Browse available courses and add them to your cart to continue.</p>
      <Button 
        className="border-0 text-white py-2 px-6 rounded-xl shadow-md hover:shadow-lg transition-all duration-200 font-semibold"
        style={{ 
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          borderRadius: '12px'
        }}
        onClick={() => navigate('/user/courses')}
      >
        <i className="fas fa-book-open me-2"></i>
        Browse Courses
      </Button>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50 p-4 sm:p-6 md:p-8 lg:p-10">
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl sm:rounded-3xl px-5 py-4 sm:p-6 border border-purple-100 shadow-xl">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl sm:rounded-2xl flex items-center justify-center text-white text-xl sm:text-2xl shadow-md">
              <i className="fas fa-shopping-cart"></i>
            </div>
            <div>
              <h2 className="text-left text-2xl sm:text-2xl font-bold text-gray-900 mb-1">My Cart</h2>
              <p className="text-gray-600 text-sm sm:text-base m-0">
                Review your selected courses and proceed to secure checkout
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="bg-white/90 backdrop-blur-sm rounded-2xl sm:rounded-3xl border border-purple-100 shadow-xl p-4 sm:p-6">
        {isLoading ? (
          <div className="text-center py-12 sm:py-16">
            <div className="inline-block animate-spin rounded-full h-12 w-12 sm:h-16 sm:w-16 border-4 border-purple-600 border-t-transparent mb-4"></div>
            <p className="text-gray-600 text-base sm:text-lg font-medium">Loading your cart...</p>
          </div>
        ) : cartItems.length === 0 ? (
          renderEmptyState()
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table hover className="mb-0">
                <thead>
                  <tr className="border-bottom-2">
                    <th className="fw-bold text-gray-700 py-3 ps-4">Course</th>
                    <th className="fw-bold text-gray-700 py-3 text-center">Price</th>
                    <th className="fw-bold text-gray-700 py-3 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {cartItems.map((item) => {
                    const price = Number(item.course?.price || 0).toFixed(2);
                    return (
                      <tr key={item.id} className="border-bottom">
                        <td className="align-middle ps-4 py-4">
                          <div className="d-flex align-items-start">
                            <div className="rounded-lg bg-gradient-to-br from-purple-100 to-indigo-100 d-flex align-items-center justify-content-center me-3 shadow-sm" style={{ width: '60px', height: '60px', minWidth: '60px' }}>
                              <i className="fas fa-book-open text-purple-600" style={{ fontSize: '20px' }}></i>
                            </div>
                            <div>
                              <h6 className="fw-bold mb-1 text-gray-900">{item.course?.name}</h6>
                              <p className="text-gray-600 mb-0 text-sm" style={{ maxWidth: '480px' }}>
                                {item.course?.description?.slice(0, 80)}{item.course?.description?.length > 80 ? '...' : ''}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="align-middle text-center fw-bold text-gray-900 py-4">
                          ₹{price}
                        </td>
                        <td className="align-middle text-center py-4">
                          <Button
                               bsPrefix="custom-hamburger"
                          className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2 "
                           
                            size="sm"
                            disabled={removingCourseId === item.courseId}
                            onClick={() => handleRemove(item.courseId)}
                          >
                            {removingCourseId === item.courseId ? (
                              <Spinner as="span" animation="border" size="sm" />
                            ) : (
                              <>
                                <i className="fas fa-trash-alt me-2"></i>
                                Remove
                              </>
                            )}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
            <div className="border-top pt-4 mt-4">
              <Row className="g-4">
                <Col md={7}>
                  <div className="p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl">
                    <div className="text-center text-gray-700 small mb-3 fw-semibold">
                      Have a voucher code? Enter it below
                    </div>
                    <Row className="g-2 align-items-center">
                      <Col md={8} sm={12}>
                        <InputGroup>
                          <InputGroup.Text className="bg-white border-2 border-gray-200">Voucher</InputGroup.Text>
                          <Form.Control
                            placeholder="Enter voucher code (if any)"
                            value={voucherInput}
                            onChange={(e) => setVoucherInput(e.target.value)}
                            readOnly={!!voucherCode}
                            className="border-2 border-gray-200 focus:border-purple-500"
                          />
                        </InputGroup>
                      </Col>
                      <Col md={4} sm={12} className="d-flex gap-2">
                        {!voucherCode ? (
                          <Button
                             bsPrefix="custom-hamburger"
                          className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2 "
                           
                          onClick={handleApplyVoucher}
                          >
                            Apply
                          </Button>
                        ) : (
                          <Button
                            variant="outline-secondary"
                                bsPrefix="custom-hamburger"
                          className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2 "
                            onClick={handleRemoveVoucher}
                          >
                            Remove
                          </Button>
                        )}
                      </Col>
                    </Row>
                    {voucherStatus && voucherMessage && (
                      <div className="mt-3">
                        <Alert
                          variant={
                            voucherStatus === 'success'
                              ? 'success'
                              : voucherStatus === 'error'
                              ? 'danger'
                              : 'info'
                          }
                          className="mb-0 py-2 rounded-lg"
                        >
                          {voucherMessage}
                        </Alert>
                      </div>
                    )}
                  </div>
                </Col>
                <Col md={5}>
                  <div className="p-4 bg-gradient-to-br from-gray-50 to-white rounded-xl border-2 border-purple-100">
                    <h5 className="fw-bold text-gray-900 mb-3">Order Summary</h5>
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-gray-600">Sub Total:</span>
                      <span className="fw-bold text-gray-900">₹{totalAmount}</span>
                    </div>
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-gray-600">Voucher Discount:</span>
                      {voucherLoading ? (
                        <Spinner as="span" animation="border" size="sm" />
                      ) : (
                        <span className={voucherDiscount > 0 ? 'text-success fw-bold' : 'text-gray-900'}>
                          {voucherDiscount > 0 ? `-₹${voucherDiscount.toFixed(2)}` : '₹0.00'}
                        </span>
                      )}
                    </div>
                    <hr className="my-3" />
                    <div className="d-flex justify-content-between mb-4">
                      <span className="fw-bold text-lg text-gray-900">Total Amount:</span>
                      <span className="fw-bold text-lg text-purple-600">₹{voucherDiscount > 0 ? totalAfterDiscount : totalAmount}</span>
                    </div>
                    <Button
                    bsPrefix="custom-hamburger"
                      className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
                   
                      onClick={handlePayNow}
                    >
                      Proceed to Payment
                      <i className="fas fa-arrow-right ms-2"></i>
                    </Button>
                  </div>
                </Col>
              </Row>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CartPage;

